<!--suppress ALL -->
<template>
	<tr :style="statusColor(item)">
		<td style="height: 100%">
			<v-icon
				small
				v-if="item.files && item.files.length"
				class="file-icon"
				style="padding-right: 5px"
			>mdi-file
			</v-icon>
			<v-icon
				small
				v-if="item.area_image && item.area_image.length"
				class="file-icon"
				style="padding-right: 5px"
			>mdi-image
			</v-icon>
			<v-icon
				small
				color="red"
				v-if="authIsManager && isDone && item.surveyor_notes"
				style="padding-right: 5px"
			>
				mdi-bell
			</v-icon>
			<v-icon small color="red" v-if="item.renew" style="padding-right: 5px">
				mdi-calendar-clock
			</v-icon>
			<v-icon small color="red" v-if="item.orders_urgent"
			>mdi-clock-fast</v-icon
			>
			<v-btn @click="open(item)" small icon text>
				<v-icon small v-if="!isOpen">mdi-chevron-down</v-icon>
				<v-icon small v-else>mdi-chevron-up</v-icon>
			</v-btn>
			<v-icon color="red" v-if="showExclamationIcon">mdi-exclamation</v-icon>
			<v-icon color="red" v-if="authIsAdmin && item.recheck"
			>mdi-sync-alert</v-icon
			>
			<v-icon size="22" color="orange" v-if="item.non_billable"
			>mdi-currency-usd-off</v-icon>
			<v-icon size="22" color="green" v-if="item.orders_is_outside"
			>mdi-arrow-bottom-left-thin-circle-outline</v-icon>
			<a :href="`/api/order/outside/confirm/${item.id}`" target="_blank">
				<v-icon size="22" color="green" v-if="item.orders_paid_at"
				>mdi-currency-usd</v-icon>
			</a>
		</td>
		<td v-if="isSurveyor || authIsManager">
			<v-checkbox v-model="isSelected" />
		</td>
		<td>
			{{ dateFormat(item.created_at) }}
		</td>
		<td>
			{{ item.orders_customer }}
		</td>
		<td>
			{{ item.orders_order_num }}
		</td>
		<td>
			<v-edit-dialog
				v-if="authIsManager"
				:return-value.sync="item.recheck"
				large
				persistent
				@save="change(item)"
			>
				<div>{{ item.recheck }}</div>
				<template v-slot:input>
					<div class="mt-4 title">Update recheck</div>
				</template>
				<template v-slot:input>
					<recheck-select
						autofocus
						:required="true"
						v-model="item.recheck"
					></recheck-select>
					<HtmSelect
						v-if="item.recheck === 'HTM'"
						v-model="item.note"
					></HtmSelect>
					<smart-input
						v-if="item.recheck === 'MNS'"
						:textarea="true"
						:rows="1"
						v-model="item.note"
						:label="$t('pages.htmForm.reasonToCancel')"
					/>
				</template>
			</v-edit-dialog>
			<div v-else>{{ item.recheck }}</div>
		</td>
		<td>
			{{ item.projects_name }}
		</td>
		<td>
			{{ item.projects_city }}
		</td>

		<td>
			{{ item.projects_address }}
		</td>

		<td>
			{{ item.orders_building }}
		</td>
		<td>
			{{ item.orders_entrance }}
		</td>
		<td>
			{{ item.order_apartment_num }}
		</td>

		<td>
			{{ item.projects_client_name }}
		</td>
		<td>
			{{ item.projects_branch_name }}
		</td>

		<td>
			{{ item.areas }}
		</td>

		<td v-if="!isSurveyor">
			<span v-if="authIsManager && (isDone || isDoAgain)">{{item.surveyor_name}}</span>
			<v-select
				v-else-if="authIsManager"
				clearable
				:items="users"
				:item-text="userName"
				item-value="id"
				v-model="item.surveyor_id"
				@change="changeSurveyor(item)"
			/>
		</td>
		<td>
			<v-text-field
				v-if="authIsManager"
				:class="isSurveyor ? 'font-weight-bold' : ''"
				:min="authIsAdmin ? minDate(item.created_at) : minDate()"
				type="datetime-local"
				v-model="startDate"
				@focusout="changeStartDate(item)"
			/>
			<p v-else>
				<span v-if="!startDate" :readonly="true">{{
					$t("pages.measurementRow.beforeCompatible")
				}}</span>
				<span v-else> {{ dateFormat(item.start_date) }}</span>
			</p>
		</td>
		<td>
			<status-select
				v-if="canChangeStatus"
				v-model.lazy="item.status"
				@input="changeStatus(item)"
			/>
			<span v-else>{{ statusString }}</span>
		</td>
		<td>
			<template v-if="authIsManager">
				<v-text-field
					v-if="endDate"
					type="datetime-local"
					:readonly="!authIsAdmin"
					v-model="endDate"
					@change="change(item)"
				/>
			</template>
			<template v-else>
				{{ endDate ? endDateFormatted : "" }}
			</template>
		</td>
		<td>
			<v-btn-toggle v-if="isSurveyor">
				<c-btn
					v-if="isSurveyor && isCoordinated"
					:tooltip-text="$t('pages.measurementRow.surveyAcceptance')"
					fab
					small
					@click="accept(item)"
				>
					<v-icon>mdi-checkbox-marked-circle</v-icon>
				</c-btn>

				<c-btn
					v-if="isSurveyor && (isCoordinated || isDone)"
					:tooltip-text="$t('pages.orderView.addOrder')"
					fab
					small
					@click="$emit('another', item)"
				>
					<v-icon>mdi-plus-circle</v-icon>
				</c-btn>
				<c-btn
					v-if="isSurveyor && isCoordinated"
					:tooltip-text="$t('attributes.notSurveyed')"
					fab
					small
					@click="$emit('cancel', item)"
				>
					<v-icon>mdi-close-circle</v-icon>
				</c-btn>
			</v-btn-toggle>
			<v-btn-toggle v-if="renewStatuses && authIsClient">
				<c-btn
					:tooltip-text="$t('attributes.reshcedule')"
					small
					@click="$emit('duplicateClient', item)"
				>
					<v-icon>mdi-calendar-clock</v-icon></c-btn
				>
			</v-btn-toggle>
			<v-btn-toggle active-class="null" v-if="canEdit">
				<edit-btn
					@click="onEdit"
					v-if="!isDone && !isDoAgain"
					:tooltip-text="$t('attributes.edit')"
				/>
				<remove-btn
					:tooltip-text="$t('attributes.archive')"
					icon="archive"
					v-if="canDelete && !item.deleted_at"
					@remove="$emit('delete', item)"
				></remove-btn>
				<c-btn
					:tooltip-text="$t('attributes.reshcedule')"
					v-if="renewStatuses"
					small
					@click="$emit('duplicateClient', item)"
				>
					<v-icon>mdi-calendar-clock</v-icon></c-btn
				>
				<c-btn
					:tooltip-text="$t('attributes.cancel')"
					v-if="!isDone && !isDoAgain"
					small
					@click="$emit('cancel', item)"
				>
					<v-icon>mdi-close-circle</v-icon>
				</c-btn>
				<c-btn
					v-if="authIsManager && (isDone || isDoAgain)"
					tooltip-text="HTM\MNS"
					small
					@click="$emit('duplicate', item)"
				>
					<v-icon>mdi-restore</v-icon>
				</c-btn>
			</v-btn-toggle>
			<v-btn-toggle v-if="item.deleted_at">
				<c-btn
					v-if="!isSurveyor"
					tooltip-text="הוצאה מארכיון"
					small
					icon
					@click="$emit('restore', item)"
				>
					<v-icon>mdi-package-up</v-icon>
				</c-btn>
				<c-btn
					v-if="authIsClient && (isDone || isDoAgain)"
					tooltip-text="מדידת חוזרת/תקן"
					small
					@click="$emit('duplicate', item)"
				>
					<v-icon>mdi-restore</v-icon>
				</c-btn>
			</v-btn-toggle>
			<v-btn
				v-if="canDownloadFtpDirectory"
				:loading="ftpDirectoryDownloading"
				small
				icon
				text
				@click="downloadFtpDirectory"
			>
				<v-icon small>mdi-folder-download</v-icon>
			</v-btn>
		</td>
	</tr>
</template>

<script>
	import { types as measurementTypes } from "src/store/modules/Measurement.module";
	import RowMixin from "src/mixins/RowMixin";
	import StatusManager, { types as statusTypes } from "dev/helpers/StatusManager";
	import StatusSelect from "dev/view/measurements/StatusSelect";
	import CBtn from "dev/scaffoldings/CBtn";
	import HtmSelect from "dev/view/measurements/HTMSelect";
	import RecheckSelect from "dev/view/orders/RecheckSelect";
	import LocaleText from "dev/components/LocaleText";
	import MeasurementService from "src/services/MeasurementService";

	export default {
		name: "measurement-row",
		props: {
			item: { required: true },
			isOpen: { default: false },
			selected: { default: false },
		},
		components: { LocaleText, RecheckSelect, HtmSelect, CBtn, StatusSelect },
		mixins: [new RowMixin(measurementTypes, "item")],
		data() {
			return {
				toggle_exclusive: null,
				openModal: false,
				ftpDirectoryDownloading: false,
			};
		},
		computed: {
			isSelected: {
				get() {
					return this.selected;
				},
				set(value) {
					this.$emit("itemSelected", { item: this.item, value });
				},
			},

			statusString() {
				return StatusManager.text(this.item.status);
			},
			beforeCoordinated() {
				return (
					this.item.status === statusTypes.NO_SCHEDULING ||
					this.item.status === statusTypes.SCHEDULING
				);
			},
			showExclamationIcon() {
				return (
					(this.isCoordinated && this.item.coordinated_note) ||
					(this.item.client_note && !this.isSurveyor) ||
					(this.item.internal_note && !this.authIsClient  &&!this.isSurveyor)
				);
			},
			endDateFormatted() {
				return moment(this.endDate).format("MM/DD/YYYY, HH:mm:ss");
			},
			isDone() {
				return (
					this.item.status === statusTypes.DONE ||
					this.item.status === statusTypes.DO_AGAIN
				);
			},
			renewStatuses(){

				return (this.item.status === statusTypes.WAITING || this.item.status === statusTypes.NOT_READY);

			},
			isDoAgain() {
				return this.item.status === statusTypes.DO_AGAIN;
			},

			isCoordinated() {
				return this.item.status === statusTypes.COORDINATED;
			},

			canDelete() {
				return this.isDone || this.authIsAdmin;
			},
			isSurveyor() {
				return this.$store.getters.isSurveyor;
			},
			authIsAdmin() {
				return this.$store.getters.isAdmin;
			},

			authIsClient() {
				return this.$store.getters.isClient;
			},
			authIsManager() {
				return this.$store.getters.isManager;
			},
			branchManager() {
				return this.$store.getters.isBranchManager;
			},

			users() {
				return this.$store.state.SurveyorModule.surveyors;
			},
			measurements() {
				return this.$store.state.MeasurementModule.measurements;
			},
			canDownloadFtpDirectory() {
				return this.item.status === statusTypes.DONE && !!(this.item.projects_branch_directory_name && this.item.orders_order_num);
			},
			canEditEndDate() {
				if (this.isSurveyor) {
					return false;
				} else {
					return this.item.status === statusTypes.DONE;
				}
			},
			endDate: {
				get() {
					return this.item.end_date ? this.item.end_date.replace(" ", "T") : null;
				},
				set(val) {
					let temp = val.replace(" ", "T");
					this.item.end_date = temp;
				},
			},
			startDate: {
				get() {
					return this.item.start_date
						? this.item.start_date.replace(" ", "T")
						: null;
				},
				set(val) {
					let temp = val.replace(" ", "T");
					this.item.start_date = temp;
				},
			},
			canEdit() {
				if (this.item.deleted_at) return false;
				else
					return (
						this.authIsManager ||
						(this.authIsClient && this.item.status < statusTypes.SCHEDULING)
					);
			},
			canChangeStatus() {
				return (
					this.authIsAdmin ||
					(this.authIsManager && !this.isDone && !this.isDoAgain)
				);
			},
		},
		methods: {
			dateFormat(date) {
				return moment(date).format("DD/MM/YY HH:mm");
			},
			minDate(date=null) {
				return moment(date).subtract(-1, "m").format("YYYY-MM-DDThh:mm");
			},

			async accept(measuremnt) {
				if (!(moment().diff(measuremnt.start_date) > 0)) {
					swal.fire({
						icon: "error",
						title: this.$t("dialog.error.title"),
						text: this.$t("dialog.error.text"),
					});
				} else {
					let item = Object.assign({}, measuremnt);

					let def = {
						title: this.$t("pages.measurementRow.surveyAcceptance"),
						text: this.$t("dialog.ok.text"),
						input: "textarea",
						inputPlaceholder: this.$t("pages.orderTable.note"),

						showCancelButton: true,
						confirmButtonText: this.$t("attributes.save"),
						confirmButtonColor: "#3085d6",
						cancelButtonColor: "#d33",
						cancelButtonText: this.$t("attributes.cancel"),
					};
					if (!item.recheck) {
						this.$emit("accept", this.item);
						return;
						let response = await swal.fire(Object.assign(def));
						if (response.dismiss) return;
						else {
							if (response.value) {
								item.surveyor_notes = response.value;
							}
							item.status = statusTypes.DONE;
							item.end_date = moment().format("YYYY-MM-DDTHH:mm:ss");
							this.changeStatus(item);
						}
					} else {
						def.text = this.$t("dialog.otherServey.text");
						def.title = this.$t("dialog.otherServey.title");
						def.input = "textarea";
						def.inputPlaceholder = this.$t("dialog.otherServey.reason");
						let { value } = await swal.fire(Object.assign(def));
						if (value) {
							item.recheck_surveyor_note = value;
							item.status = statusTypes.DONE;
							item.end_date = moment().format("YYYY-MM-DDTHH:mm:ss");
							this.$store.dispatch(measurementTypes.UPDATE, item);
						}
					}
				}
			},
			open(item) {
				this.$emit("open", item);
			},
			userName(item) {
				return [item.name, item.last_name].join(" ");
			},
			statusColor(item) {
				let style = {
					backgroundColor: StatusManager.color(item.status),
				};
				return style;
			},
			statusReduction(item, status) {
				if (status === statusTypes.COORDINATED) {
					if (item.surveyor_id) {
						item.status = statusTypes.SCHEDULING;
					} else {
						item.status = statusTypes.NO_SCHEDULING;
					}
				}
				return item;
			},
			changeStartDate(item) {
				this.$nextTick(() => {
					//להשלים אכיפה שנדרש להכניס שעה ודקות - לא עובד תמיד
					if (!item.start_date) {
						swal.fire({
							title: this.$t("dialog.error.wrongDate"),
							text: this.$t("dialog.error.wrongDateText"),
						});
						this.statusReduction(item, statusTypes.COORDINATED);
						item.start_date = null;
					} else if (moment(item.start_date).isBefore()) {
						swal.fire({
							type: "error",
							title: this.$t("dialog.error.title"),
							text: this.$t("dialog.error.beforeDate"),
						});
						item.start_date = null;
					} else {
						if (!item.start_date) {
							this.statusReduction(item, statusTypes.COORDINATED);
						}
						this.$store.dispatch(measurementTypes.UPDATE, item);
					}
				});
			},
			async changeStatus(item) {
				setTimeout(() => {
					if (
						item.status == statusTypes.COORDINATED &&
						(!item.start_date || !item.surveyor_id)
					) {
						swal.fire(this.$t("dialog.error.toCompate"));
						if (item.surveyor_id) {
							item.status = statusTypes.SCHEDULING;
						} else {
							item.status = statusTypes.NO_SCHEDULING;
						}
						return;
					}
					if (
						[statusTypes.DONE, statusTypes.DO_AGAIN].indexOf(item.status) > -1 &&
						!item.end_date
					) {
						item.end_date = moment().format("YYYY-MM-DDTHH:mm:ss");
					} else if (
						[statusTypes.DONE, statusTypes.DO_AGAIN].indexOf(item.status) === -1
					) {
						item.end_date = null;
					}
					this.$store.dispatch(measurementTypes.UPDATE, item);
				}, 0);
			},

			change() {
				this.$store.dispatch(measurementTypes.UPDATE, this.item);
			},

			changeSurveyor(item) {
				if (!item.surveyor_id) {
					item.surveyor_id = null;
					item.status = statusTypes.NO_SCHEDULING;
				} else {
					if (item.status == statusTypes.NO_SCHEDULING) {
						item.status = statusTypes.SCHEDULING;
					}
				}

				this.$store.dispatch(measurementTypes.UPDATE, item);
			},
			async downloadFtpDirectory() {
				this.ftpDirectoryDownloading = true;
				try {
					let response = await MeasurementService.downloadFtpDirectory(this.item.id);
					let url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
					let link = document.createElement("a");
					link.href = url;
					link.download = `measurement-${this.item.orders_order_num}.zip`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);
				} catch (error) {
					if (error.response && error.response.status === 404) {
						swal.fire("הקובץ לא נמצא", "", "error");
						return;
					}

					throw error;
				} finally {
					this.ftpDirectoryDownloading = false;
				}
			},
		},
	};
</script>

<style scoped>
th {
  padding: 1px;
}

td {
  font-size: 14px !important;
  padding: 0px;
  margin: 0px;
}

.v-text-field {
  font-size: 14px !important;
}
</style>
